export interface TrackingConfig {
  gtmEnabled?: boolean;
  gtmId?: string | null;
  ga4Enabled?: boolean;
  ga4MeasurementId?: string | null;
  metaPixelEnabled?: boolean;
  metaPixelId?: string | null;
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

/**
 * Inizializza dinamicamente gli script di tracciamento abilitati nel DOM.
 */
export function initTracking(config: TrackingConfig): void {
  if (typeof window === "undefined") return;

  // 1. GOOGLE TAG MANAGER
  if (
    config.gtmEnabled &&
    config.gtmId &&
    !document.getElementById("gtm-script")
  ) {
    const gtmId = config.gtmId.trim();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    document.head.appendChild(script);
  }

  // 2. GOOGLE ANALYTICS 4
  if (
    config.ga4Enabled &&
    config.ga4MeasurementId &&
    !document.getElementById("ga4-script")
  ) {
    const ga4Id = config.ga4MeasurementId.trim();
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };
    window.gtag("js", new Date());
    window.gtag("config", ga4Id);

    const script = document.createElement("script");
    script.id = "ga4-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`;
    document.head.appendChild(script);
  }

  // 3. META PIXEL
  if (
    config.metaPixelEnabled &&
    config.metaPixelId &&
    !document.getElementById("meta-pixel-script")
  ) {
    const pixelId = config.metaPixelId.trim();

    if (!window.fbq) {
      const fbq: any = function () {
        if (fbq.callMethod) {
          fbq.callMethod.apply(fbq, arguments);
        } else {
          fbq.queue.push(arguments);
        }
      };
      if (!window._fbq) window._fbq = fbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      window.fbq = fbq;
    }

    window.fbq("init", pixelId);

    const script = document.createElement("script");
    script.id = "meta-pixel-script";
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }
}

/**
 * Traccia l'apertura della pagina
 */
export function trackPageView(
  config: TrackingConfig,
  pageTitle = "Richiesta Preventivo",
): void {
  if (typeof window === "undefined") return;

  const url = window.location.href;

  if (config.gtmEnabled && window.dataLayer) {
    window.dataLayer.push({
      event: "page_view",
      page_title: pageTitle,
      page_location: url,
    });
  }

  if (config.ga4Enabled && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_title: pageTitle,
      page_location: url,
    });
  }

  if (config.metaPixelEnabled && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
}

/**
 * Traccia la selezione completa delle date (check-in e check-out)
 */
export function trackDatesSelected(
  config: TrackingConfig,
  checkin: Date,
  checkout: Date,
): void {
  if (typeof window === "undefined") return;

  const checkinStr = checkin.toISOString().split("T")[0];
  const checkoutStr = checkout.toISOString().split("T")[0];
  const diffTime = Math.abs(checkout.getTime() - checkin.getTime());
  const nights = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

  const eventData = {
    checkin: checkinStr,
    checkout: checkoutStr,
    nights: nights,
  };

  if (config.gtmEnabled && window.dataLayer) {
    window.dataLayer.push({
      event: "dates_selected",
      ...eventData,
    });
  }

  if (config.ga4Enabled && typeof window.gtag === "function") {
    window.gtag("event", "dates_selected", eventData);
  }

  if (config.metaPixelEnabled && typeof window.fbq === "function") {
    window.fbq("trackCustom", "DatesSelected", eventData);
  }
}

/**
 * Traccia l'invio del form di preventivo a validazione superata
 */
export function trackFormSubmitted(
  config: TrackingConfig,
  details: {
    checkin?: Date;
    checkout?: Date;
    totalGuests?: number;
    accommodationsCount?: number;
  },
): void {
  if (typeof window === "undefined") return;

  const checkinStr = details.checkin
    ? details.checkin.toISOString().split("T")[0]
    : undefined;
  const checkoutStr = details.checkout
    ? details.checkout.toISOString().split("T")[0]
    : undefined;
  const nights =
    details.checkin && details.checkout
      ? Math.max(
          1,
          Math.ceil(
            Math.abs(details.checkout.getTime() - details.checkin.getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : undefined;

  const leadData = {
    currency: "EUR",
    checkin: checkinStr,
    checkout: checkoutStr,
    nights: nights,
    total_guests: details.totalGuests,
    accommodations_count: details.accommodationsCount,
  };

  if (config.gtmEnabled && window.dataLayer) {
    window.dataLayer.push({
      event: "generate_lead",
      ...leadData,
    });
  }

  if (config.ga4Enabled && typeof window.gtag === "function") {
    window.gtag("event", "generate_lead", leadData);
  }

  if (config.metaPixelEnabled && typeof window.fbq === "function") {
    window.fbq("track", "Lead", {
      content_name: "Preventive Request",
      ...leadData,
    });
  }
}
